/**
 * 뉴스 스타일 SearchBar 컴포넌트
 * 카테고리 드롭다운 + 검색바 + 검색 버튼
 * 위치 찾기는 탑 네비게이션에서만 사용
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { searchCategories, type SearchCategory } from "@/lib/searchCategories";
import { ChevronDown, Search } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

interface NewSearchBarProps {
  onSearch?: (query: string, category?: string) => void;
  defaultCategory?: string;
  categories?: Array<{ id: string; label: string }>;
}

export function NewSearchBar({ onSearch, defaultCategory, categories }: NewSearchBarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const displayCategories = categories || searchCategories;
  const [selectedCategory, setSelectedCategory] =
    useState<{ id: string; label: string } | null>(
      displayCategories.find((c) => c.id === defaultCategory) || null
    );
  const [, setLocation] = useLocation();
  const popularKeywords =
    defaultCategory === "community-talk"
      ? ["본드 못 받았어요", "TFN 안 왔어요", "신청 거절", "비자 기한", "급여 미지급", "학교 등록"]
      : defaultCategory === "meetup"
        ? ["동네친구", "스터디", "운동", "육아", "독서", "주말 행사"]
        : [
            "자동차",
            "렌트",
            "에어컨",
            "노트북",
            "아이폰",
            "가구",
            "영어과외",
            "한식당",
            "부동산",
            "이삿짐",
            "통역",
            "회계사",
          ];

  const handleSearch = (queryOverride?: string) => {
    const query = (queryOverride ?? searchQuery).trim();
    if (query) {
      // onSearch 콜백이 있으면 현재 페이지에서 필터링, 없으면 검색 페이지로 이동
      if (onSearch) {
        onSearch(query, selectedCategory?.id);
      } else {
        const categoryParam = selectedCategory
          ? `&category=${selectedCategory.id}`
          : "";
        setLocation(
          `/search?q=${encodeURIComponent(query)}${categoryParam}`
        );
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="border-b border-border/70 bg-background py-3 sm:py-4">
      <div className="mx-auto w-full max-w-[1536px] px-4 md:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          {/* 검색바 */}
          <div className="flex min-w-0 flex-1 items-center gap-0 overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-primary/20">
            {/* 카테고리 드롭다운 (검색바 내부 왼쪽) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-11 max-w-32 shrink-0 items-center gap-1.5 border-r border-border px-3 text-sm text-foreground transition-colors hover:bg-muted/50 sm:h-12 sm:max-w-44">
                  <span className="truncate">
                    {selectedCategory ? selectedCategory.label : "전체"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-48 bg-popover border-border"
              >
                <DropdownMenuItem
                  onClick={() => setSelectedCategory(null)}
                  className="text-popover-foreground hover:bg-muted"
                >
                  전체
                </DropdownMenuItem>
                {displayCategories.map((category) => (
                  <DropdownMenuItem
                    key={category.id}
                    onClick={() => setSelectedCategory(category)}
                    className="text-popover-foreground hover:bg-muted"
                  >
                    {category.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 검색어 입력 필드 */}
            <Input
              type="text"
              placeholder="검색어를 입력해주세요"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              className="h-11 min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 sm:h-12 sm:px-4 sm:text-base"
            />

            {/* 검색 버튼 (사각형, 오른쪽) - 흰색 배경, 검은색 아이콘 - 85% 크기 */}
            <button
              onClick={() => handleSearch()}
              aria-label="검색"
              className="mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 sm:h-10 sm:w-10"
            >
              <Search className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* 인기 검색어 */}
        <div className="mt-2.5 flex gap-1 overflow-x-auto whitespace-nowrap pb-1 text-xs text-muted-foreground [scrollbar-width:none] sm:text-sm [&::-webkit-scrollbar]:hidden">
          <span className="font-semibold text-foreground">인기 검색어: </span>
          {popularKeywords.map((keyword, index, array) => (
            <span key={keyword}>
              <button
                onClick={() => {
                  setSearchQuery(keyword);
                  handleSearch(keyword);
                }}
                className="hover:text-[#60a6fa] transition-colors cursor-pointer"
              >
                {keyword}
              </button>
              {index < array.length - 1 && (
                <span className="text-muted-foreground/60"> · </span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
